// src/pages/SignUp.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import "./SignUp.css";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../components/firebaseConfig";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[A-Za-z0-9._]+$/;


const API_BASE_URL = process.env.REACT_APP_BASE_URL;


const SignUp = () => {
    const navigate = useNavigate();
    const debounceRef = useRef(null);
    const emailDebounceRef = useRef(null);



    const [email, setEmail] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
    const [usernameTouched, setUsernameTouched] = useState(false);
    const [agreeToPolicies, setAgreeToPolicies] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [displayNameTouched, setDisplayNameTouched] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [emailChecking, setEmailChecking] = useState(false);
    const [emailTaken, setEmailTaken] = useState(false);
    const [emailCheckError, setEmailCheckError] = useState("");
    const [usernameCheckError, setUsernameCheckError] = useState("");


    function usernameError(u) {
        if (!u) return "Username is required.";
        const v = u.trim();
        if (v.length >= 30 || v.length < 3) return "Usernames have to be less than 30 characters and more than 2 characters.";
        if (!USERNAME_RE.test(v)) return "Usernames can only have letters, numbers, periods, and underscores.";
        if (v.startsWith(".") || v.endsWith(".")) return "Usernames can’t start or end with a period.";
        if (v.includes("..")) return "Usernames can’t have consecutive periods.";
        return ""; // looks good
    }

    function passwordError(p) {
        if (!p) return "Password is required.";
        if (p.length < 8 || !/[A-Za-z]/.test(p) || !/\d/.test(p))
            return "Password must be at least 8 characters and include at least 1 letter and 1 number.";
        if (p.length > 72)
            return "Password must be less than 72 characters.";
        return "";
    }


    const validateFields = () => {
        const emailNorm = email.trim().toLowerCase();
        if (!EMAIL_RE.test(emailNorm) || emailNorm.length > 254) return "Please enter a valid email address.";
        if (displayName.trim().length < 2 || displayName.trim().length > 50) return "Display name must be 2–50 characters.";
        const uErr = usernameError(username);
        if (uErr) return uErr;
        if (usernameStatus === 'taken') return "Please enter an available username"
        const pErr = passwordError(password);
        if (pErr) return pErr;
        if (!agreeToPolicies) return "Please agree to the Privacy Policy and Terms and Conditions.";
        return "";
    };

    const checkUsername = async (value) => {
        setUsernameCheckError("");
        setUsernameStatus('checking');
        try {
            const res = await fetch(`${API_BASE_URL}/api/username/check?username=${value}`);
            const json = await res.json();
            if (!res.ok) {
                const msg = res.status === 429 ? "Too many attempts. Please wait a few minutes and try again." : (json?.error || "Could not verify username.");
                setUsernameStatus(null);
                setUsernameCheckError(msg);
                return;
            }
            if (json.available) {
                setUsernameStatus('available');
            } else {
                setUsernameStatus('taken');
            }
        } catch (err) {
            setUsernameStatus(null);
            setUsernameCheckError("Could not verify username. Please try again.");
        }
    };

    const checkEmail = async (value) => {
        const normalized = value.trim().toLowerCase();
        setEmailTaken(false);
        setEmailCheckError("");
        if (!EMAIL_RE.test(normalized)) return;
        try {
            setEmailChecking(true);
            const res = await fetch(`${API_BASE_URL}/api/email/check?email=${encodeURIComponent(normalized)}`);
            const json = await res.json();
            if (!res.ok) throw new Error(json?.error || "Could not verify email.");
            setEmailTaken(json?.available === false);
        } catch (e) {
            setEmailTaken(false);
            setEmailCheckError(e.message || "Could not verify email. Please try again.");
        } finally {
            setEmailChecking(false);
        }
    };

    const emailNorm = email.trim().toLowerCase();
    const emailErr = email && !EMAIL_RE.test(emailNorm) ? "Please enter a valid email address." : "";
    const emailTakenErr = emailTaken ? "An account already exists with this email." : "";
    const displayNameErr = displayName && (displayName.trim().length < 2 || displayName.trim().length > 50)
        ? "Display name must be 2–50 characters."
        : "";
    const usernameErr = usernameError(username);
    const usernameTakenErr = usernameStatus === 'taken' ? "Please enter an available username." : "";
    const usernameInlineErr = usernameCheckError || usernameErr || usernameTakenErr;
    const passwordInlineErr = password ? passwordError(password) : "";

    const displayNameOk = displayName.trim().length >= 2 && displayName.trim().length <= 50;
    const canSubmit =
        EMAIL_RE.test(emailNorm) &&
        emailNorm.length <= 254 &&
        !emailTaken &&
        displayNameOk &&
        !usernameErr &&
        usernameStatus !== "taken" &&
        !usernameCheckError &&
        !passwordError(password) &&
        agreeToPolicies &&
        !submitting;

    useEffect(() => {
        if (!emailTouched) return;
        if (!EMAIL_RE.test(emailNorm)) {
            setEmailTaken(false);
            setEmailCheckError("");
            return;
        }
        clearTimeout(emailDebounceRef.current);
        emailDebounceRef.current = setTimeout(() => checkEmail(emailNorm), 450);
        return () => clearTimeout(emailDebounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, emailNorm, emailTouched]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");

        const v = validateFields();
        if (v) return setErr(v);

        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/apply`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: emailNorm, displayName, username, password })
            });
            const json = await res.json();
            if (!res.ok) {
                setErr(json?.error || "Something went wrong.");
                setSubmitting(false);
                return;
            }
            await signInWithEmailAndPassword(auth, emailNorm, password);
            navigate("/create/listing");
        } catch {
            setErr("Network error. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <div className="signup-container">
            <h2 className="signup-title">Bring Your Presence to Life</h2>
            <form className="signup-form" onSubmit={onSubmit}>
                <div className="signup-field">
                    <input
                        type="text"
                        placeholder="Email"
                        className="signup-input"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setEmailTouched(true);
                            setEmailTaken(false);
                        }}
                        onBlur={() => {
                            setEmailTouched(true);
                            checkEmail(email);
                        }}
                    />
                    {emailTouched && (emailErr || emailTakenErr || emailCheckError) && (
                        <div className="signup-field-error">{emailErr || emailTakenErr || emailCheckError}</div>
                    )}
                    {emailChecking && !emailErr && !emailTakenErr && !emailCheckError && (
                        <div className="signup-field-hint">Checking email…</div>
                    )}
                </div>
                <div className="signup-field">
                    <input
                        type="text"
                        placeholder="Display Name"
                        className="signup-input"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        onBlur={() => setDisplayNameTouched(true)}

                    />
                    {displayNameTouched && displayNameErr && <div className="signup-field-error">{displayNameErr}</div>}
                    <small className="signup-helper">
                        The name people see on your listing page
                    </small>
                </div>
                <div className="signup-field">
                    <input
                        type="text"
                        placeholder="Username"
                        className="signup-input"
        value={username}
        onChange={(e) => {
            const value = e.target.value.toLocaleLowerCase();
            setUsername(value);
            setUsernameStatus(null);
            setUsernameCheckError("");
            clearTimeout(debounceRef.current);
            if (usernameError(value)) return; // skip if error locally
            debounceRef.current = setTimeout(() => {
                checkUsername(value);
            }, 500);
                        }}
                        onBlur={() => setUsernameTouched(true)}
                    />
                    {username && !usernameError(username) && !usernameCheckError && (
                        <div className="signup-username-status">
                            {usernameStatus === 'checking' && <span className="signup-username-check-text">Checking…</span>}
                            {usernameStatus === 'available' && <span className="signup-username-check-text" style={{ color: 'green' }}>✅ Available</span>}
                            {usernameStatus === 'taken' && <span className="signup-username-check-text" style={{ color: 'red' }}>❌ Already taken</span>}
                        </div>
                    )}
                    {usernameTouched && usernameInlineErr && (
                        <div className="signup-field-error">{usernameInlineErr}</div>
                    )}
                    <small className="signup-helper">
                        This will be part of your link: activitybuddy.me/{username || 'Username'}
                    </small>
                </div>
                <div className="signup-field signup-password-wrap">
                    <div className="signup-password-inner">
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            className="signup-input signup-input-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onBlur={() => setPasswordTouched(true)}
                        />
                        <span
                            className="toggle-password"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                        </span>
                    </div>
                    {passwordTouched && passwordInlineErr && <div className="signup-field-error">{passwordInlineErr}</div>}
                </div>
                {err && <div className="signup-error-message">{err}</div>}

                <div className="signup-field signup-consent">
                    <label className="signup-consent-label">
                        <input
                            type="checkbox"
                            checked={agreeToPolicies}
                            onChange={(e) => setAgreeToPolicies(e.target.checked)}
                        />
                        <span>
                            I agree to the{" "}
                            <Link to="/privacy" className="signup-consent-link" target="_blank" rel="noreferrer">Privacy Policy</Link>{" "}
                            and{" "}
                            <Link to="/terms" className="signup-consent-link" target="_blank" rel="noreferrer">Terms and Conditions</Link>.
                        </span>
                    </label>
                </div>

                <button type="submit" className="signup-btn" disabled={!canSubmit}>
                    {submitting ? "Creating…" : "Continue"}
                </button>
            </form>
        </div>
    );
};

export default SignUp;
